/**
 * Policy Acknowledgment Service
 *
 * Covers two concerns:
 *   1. Staff-side: create acknowledgment campaigns via authenticated API (JWT)
 *   2. Driver-side: fetch + sign via public token-gated endpoints (no auth)
 */
import axios from 'axios'
import { api } from './api'

// --- Staff-side (uses auth-intercepted `api` instance) ---

export interface CreateCampaignResponse {
  campaign_id: string
  drivers_targeted: number
  sent_at: string
}

// --- Driver-side types (public, no auth) ---

export interface PolicyAcknowledgmentTokenData {
  policy_type: string
  policy_label: string
  policy_pdf_url: string
  client_legal_name: string
  driver_name: string
  signed_at: string | null
}

export interface PolicySignResponse {
  signed_at: string
}

// Public axios — no auth interceptor, just the base URL.
const PUBLIC_API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://api.claimshieldpro.com/api'

const publicAxios = axios.create({ baseURL: PUBLIC_API_BASE })

export const policyAcknowledgmentService = {
  /**
   * Create an acknowledgment campaign targeting all active drivers of a client.
   * Staff/admin only — uses the JWT-authenticated `api` instance.
   */
  async createCampaign(
    clientId: string,
    documentId: string,
    audience: 'active_drivers' = 'active_drivers',
  ): Promise<CreateCampaignResponse> {
    const { data } = await api.post<CreateCampaignResponse>(
      `/v1/clients/${clientId}/documents/${documentId}/send-acknowledgments`,
      { audience },
    )
    return data
  },

  /**
   * Fetch policy metadata for a driver via their magic-link token.
   * Public — no JWT required.
   */
  async fetchByToken(token: string): Promise<PolicyAcknowledgmentTokenData> {
    const { data } = await publicAxios.get<PolicyAcknowledgmentTokenData>(
      `/v1/policy-acknowledgments/${token}`,
    )
    return data
  },

  /**
   * Submit a driver's signature for a policy acknowledgment.
   * Public — no JWT required.
   */
  async sign(
    token: string,
    signaturePngBase64: string,
    typedName: string,
  ): Promise<PolicySignResponse> {
    const { data } = await publicAxios.post<PolicySignResponse>(
      `/v1/policy-acknowledgments/${token}/sign`,
      {
        signature_png_base64: signaturePngBase64,
        typed_name: typedName,
      },
    )
    return data
  },
}
